// Download Details Section - Public download details page
// Follows the Section, Component & Manager System architecture

import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { DownloadsManager } from '../../managers/downloads-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { DownloadDetailsLayout } from './components/DownloadDetailsLayout';
import './DownloadDetailsSection.css';

export function DownloadDetailsSection() {
  const { manager, isInitialized } = useManager(DownloadsManager);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [download, setDownload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const downloadId = searchParams.get('downloadId') || '';

  const loadDownload = useCallback(async (id) => {
    if (!id || !manager) return;
    setLoading(true);
    setError(null);
    try {
      const data = await manager.getDownloadById(id);
      setDownload(data);
      // Track view after successful load and optimistically update UI
      if (data) {
        const didTrack = await manager.trackView(id);
        if (didTrack) {
          setDownload(prev => prev ? { ...prev, views: (prev.views || 0) + 1 } : prev);
        }
      }
    } catch (err) {
      console.error('Failed to load download:', err);
      setError(err.message || 'Failed to load download');
      setDownload(null);
    } finally {
      setLoading(false);
    }
  }, [manager]);

  useEffect(() => {
    if (isInitialized && manager && downloadId) {
      loadDownload(downloadId);
    } else if (!downloadId) {
      setDownload(null);
      setLoading(false);
      setError(null);
    } else {
      // Have downloadId but waiting for manager init - show loading
      setDownload(null);
      setLoading(true);
      setError(null);
    }
  }, [isInitialized, manager, downloadId, loadDownload]);

  const handleBackToDownloads = useCallback((e) => {
    e.preventDefault();
    navigate('/downloads');
  }, [navigate]);

  const handleAdminClick = useCallback((e) => {
    e.preventDefault();
    navigate(`/downloads/admin/details?downloadId=${downloadId}`);
  }, [navigate, downloadId]);

  return (
    <MainLayout contentClassName="main-content--download-details">
      <div className="download-details-section">
        <div className="download-details-header">
          <div className="download-details-controls-row">
            <button
              className="downloads-admin-btn downloads-back-btn"
              onClick={handleBackToDownloads}
              title="Back to Downloads"
            >
              <i className="fa-solid fa-arrow-left"></i>
              Back to Downloads
            </button>
            {user?.role === 'admin' && (
              <button
                className="downloads-admin-btn"
                onClick={handleAdminClick}
                title="Manage Download (Admin)"
              >
                <i className="fa-solid fa-cog"></i>
                Admin
              </button>
            )}
          </div>
        </div>

        <div className="download-details-content">
          {loading ? (
            <div className="download-details-loading-state">
              <div className="download-details-loading-content">
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Loading download...</span>
              </div>
            </div>
          ) : error ? (
            <div className="download-details-error-state">
              <div className="download-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Failed to load download</h3>
                <p>{error}</p>
                <p><small>Download ID: {downloadId}</small></p>
              </div>
            </div>
          ) : download ? (
            <DownloadDetailsLayout
              download={download}
              onDownloadTracked={() => {
                // Optimistically increment downloads in local state
                setDownload(prev => prev ? { ...prev, downloads: (prev.downloads || 0) + 1 } : prev);
              }}
            />
          ) : (
            <div className="download-details-error-state">
              <div className="download-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Download not found</h3>
                <p>The requested download could not be found.</p>
                <p><small>Download ID: {downloadId}</small></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
